import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {ActionMeta} from 'react-select';
import * as yup from 'yup';

import {PermissionAccess} from 'components/common/PermissionAccess';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {COUNTRY_CODES} from 'core/constants/countryCodes';
import {IS_NG} from 'core/constants/env';
import {ROLES_MAP} from 'core/constants/roleMatrix';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {$userRoles, profile$, profileState$} from 'models/auth';
import {
    data$,
    setUpdateParticipantRolesModalVisible,
    updateParticipantRolesModalVisible$,
} from 'models/profile/common';
import {ParticipantRegistrationRequest, UserPermission} from 'types';
import {ProfileRole} from 'types/profile';

import {CredentialsFIRSForm} from './FIRS';
import {CommonHeading, CustomContentContainer, Field, FormFields} from './styled';
import {TypeConfirmationModal} from './TypeConfirmationModal';

type Opt = {value: ProfileRole; label: string};

export const CommonData = () => {
    const userRoles = useStore($userRoles);
    const updateParticipantRolesModalVisible = useStore(updateParticipantRolesModalVisible$);
    const [shouldShow, setShouldShow] = useState(false);

    const {t, i18n} = useTranslation();

    const profile = useStore(profile$);
    const common = useStore(data$);
    const state = useStore(profileState$);

    const valuesToUpdate = useRef<{add: ProfileRole[]; remove: ProfileRole[]}>({
        add: [],
        remove: [],
    });

    useEffect(() => {
        if (!shouldShow) return;
        setUpdateParticipantRolesModalVisible(true);
        setShouldShow(false);
    }, [shouldShow]);

    const typeOptions = useMemo(
        () => [
            {value: ProfileRole.MANUFACTURER, label: t('dictionaries.profileRole.MANUFACTURER')},
            {value: ProfileRole.IMPORTER, label: t('dictionaries.profileRole.IMPORTER')},
            {value: ProfileRole.DISTRIBUTOR, label: t('dictionaries.profileRole.DISTRIBUTOR')},
            {value: ProfileRole.RETAILER, label: t('dictionaries.profileRole.RETAILER')},
        ],
        [userRoles, t],
    );

    useEffect(() => {
        if (!updateParticipantRolesModalVisible) {
            valuesToUpdate.current = {
                add: [],
                remove: [],
            };

            const profileTypes = Array.from(
                new Set(userRoles?.map((role) => ROLES_MAP[role]).filter(Boolean) ?? []),
            );
            const values = profileTypes
                .map((type) => typeOptions.find((o) => o.value === type))
                .filter(Boolean) as Opt[];

            setValue('type', values);
        }
    }, [updateParticipantRolesModalVisible, userRoles]);

    const fieldsConfig = useMemo<Record<string, any>>(() => {
        const countryCode = state.profile?.legalPerson?.country;
        const country = COUNTRY_CODES.find(({value}) => value === countryCode)?.label;

        return {
            fullName: state.profile?.legalPerson?.company_name || '—',
            shortName: state.profile?.legalPerson?.short_name || '—',
            tin: state.profile?.legalPerson?.tin || '—',
            gln: state.profile?.legalPerson?.gln || '—',
            vatId: state.profile?.legalPerson?.vat_id || '—',
            country: country || countryCode || '—',
            legalAddress: state.profile?.legalPerson?.legal_address || '—',
            ceo: state.profile?.fullName || '—',
            // type: common?.type ? t(`dictionaries.profileRole.${common.type}`) : '—',
            postalCode: state.profile?.legalPerson?.postal_code || '—',
            gcp: state.profile?.client?.gcp || '—',
        };
    }, [state.profile, common, t]);

    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );

    useEffect(() => {
        if (profile) {
            setValue('first_name', profile?.firstName);
            setValue('last_name', profile?.lastName);
            setValue('role', profile?.client?.role);
            setValue('email', profile?.email);
        } else reset();
    }, [profile]);

    const participantFormSchema = yup.object().shape({
        storageName: yup.string().required(translateValidates('required')),
        storageGln: yup.string().notRequired(),
        storageTypeId: yup
            .number()
            .typeError(translateValidates('required'))
            .required(translateValidates('required')),
        userIds: yup
            .array()
            .typeError(translateValidates('required'))
            .of(yup.number().required(translateValidates('required')))
            .min(1, translateValidates('required')),
        storageAddress: yup.string().required(translateValidates('required')),
    });

    const {control, reset, setValue} = useForm<ParticipantRegistrationRequest>({
        resolver: yupResolver(participantFormSchema),
        mode: 'all',
    });

    const roles = useMemo(() => {
        const uniqRoles = new Set(userRoles?.map((role) => ROLES_MAP[role]).filter(Boolean));
        return Array.from(uniqRoles)
            .map((role) => t(`dictionaries.profileRole.${role}`))
            .join(', ');
    }, [userRoles, t]);

    return (
        <>
            <TypeConfirmationModal
                data={valuesToUpdate.current}
                legalIdentityId={profile?.legalPerson?.id}
            />
            <CustomContentContainer status={state.loading ? 'loading' : 'default'}>
                <CommonHeading>
                    {t('pages.profile.pills.CommonData')}
                    <Button
                        variant={ButtonVariant.Outline}
                        onClick={() => history.push(ROUTE.CHANGE_PASSWORD)}
                    >
                        {t('pages.password.change.changeButton')}
                    </Button>
                </CommonHeading>
                <form>
                    <FormFields customGrid="repeat(6, 1fr)" spanLastTwoFields={true}>
                        {Object.entries(fieldsConfig).map(([key, value]) => (
                            <Field key={key}>
                                <span>{t(`pages.profile.common.${key}`)}</span>
                                <Input
                                    readOnly
                                    placeholder={t(`pages.profile.common.${key}`)}
                                    formFieldName={key}
                                    value={value}
                                />
                            </Field>
                        ))}
                        <PermissionAccess permissions={[UserPermission.ROLE_TYPE_VIEW]}>
                            <Field>
                                <span>{t('pages.profile.common.roles')}</span>
                                <Input readOnly value={roles} />
                            </Field>
                        </PermissionAccess>
                        <PermissionAccess permissions={[UserPermission.ROLE_TYPE_EDIT]}>
                            <Field>
                                <span>{t('pages.profile.common.roleTypeLablel')}</span>
                                <Controller
                                    name="type"
                                    control={control}
                                    defaultValue={null}
                                    render={({value, onChange, ...rest}) => {
                                        const selected = (value ?? [])
                                            .map((v: Opt) =>
                                                typeOptions.find(
                                                    (o) =>
                                                        o.value ===
                                                        (typeof v === 'object' ? v.value : v),
                                                ),
                                            )
                                            .filter(Boolean) as Opt[];

                                        return (
                                            <Select
                                                {...rest}
                                                value={selected}
                                                isMulti
                                                variant="checkbox"
                                                isClearable={false}
                                                backspaceRemovesValue={false}
                                                components={{
                                                    MultiValueRemove: () => null,
                                                }}
                                                closeMenuOnSelect={false}
                                                onMenuClose={() => {
                                                    onChange(value);
                                                    const {add, remove} = valuesToUpdate.current;
                                                    if (add.length > 0 || remove.length > 0) {
                                                        setShouldShow(true);
                                                    }
                                                }}
                                                isSearchable={false}
                                                options={typeOptions}
                                                onChange={(
                                                    newValue: Opt[],
                                                    actionMeta: ActionMeta<Opt>,
                                                ) => {
                                                    const next = (newValue ?? []) as Opt[];
                                                    const isDeselection =
                                                        actionMeta.action === 'deselect-option' ||
                                                        actionMeta.action === 'remove-value' ||
                                                        actionMeta.action === 'pop-value' ||
                                                        actionMeta.action === 'clear';
                                                    if (isDeselection && next.length === 0) return;

                                                    const {add, remove} = valuesToUpdate.current;
                                                    if (
                                                        actionMeta.action === 'select-option' &&
                                                        actionMeta.option
                                                    ) {
                                                        const v = actionMeta.option.value;
                                                        const idx = remove.indexOf(v);
                                                        if (idx !== -1) {
                                                            remove.splice(idx, 1);
                                                        } else if (!add.includes(v)) {
                                                            add.push(v);
                                                        }
                                                    } else if (
                                                        actionMeta.action === 'deselect-option' &&
                                                        actionMeta.option
                                                    ) {
                                                        const v = actionMeta.option.value;
                                                        const idx = add.indexOf(v);
                                                        if (idx !== -1) {
                                                            add.splice(idx, 1);
                                                        } else if (!remove.includes(v)) {
                                                            remove.push(v);
                                                        }
                                                    }

                                                    onChange(newValue);
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Field>
                        </PermissionAccess>
                    </FormFields>
                </form>
                {IS_NG && <CredentialsFIRSForm />}
            </CustomContentContainer>
        </>
    );
};
